import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import { Button } from '@scio3/ui';
import { useTeamStore } from '@/stores/team-store';
import { CanvasThumbnail } from './CanvasThumbnail';

export interface DesignCardData {
  designId: string;
  title: string;
  orientation: 'portrait' | 'landscape';
  dimensions: { width: number; height: number };
}

/**
 * Card displaying a single AI-generated design inside OptiBot chat.
 * Renders a real canvas thumbnail via Fabric.js StaticCanvas.
 */
export function DesignCard({ designId, title, orientation, dimensions }: DesignCardData) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const teamId = useTeamStore((s) => s.activeTeamId);

  const designUrl = `/app/designManagement?ai_design_id=${designId}&teamId=${teamId}&origin=ai-templates`;
  const dimensionLabel = `${dimensions.width}\u00D7${dimensions.height}`;
  const orientationLabel =
    orientation === 'portrait'
      ? t('optibot.designCard.portrait')
      : t('optibot.designCard.landscape');

  return (
    <div
      className="overflow-hidden rounded-lg border border-border bg-background"
      data-testid="optibot-design-card"
    >
      {/* Canvas thumbnail — rendered via Fabric.js StaticCanvas */}
      <CanvasThumbnail designId={designId} onClick={() => navigate(designUrl)} />

      {/* Info + actions */}
      <div className="flex items-center justify-between gap-2 px-2.5 py-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium leading-tight">{title}</p>
          <p className="mt-0.5 text-[10px] text-muted-foreground">
            {orientationLabel} · {dimensionLabel}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            variant="default"
            size="sm"
            className="h-6 px-2 text-[10px]"
            onClick={() => navigate(designUrl)}
          >
            {t('optibot.designCard.openInDesigner')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            aria-label={t('optibot.designCard.openInNewTab')}
            onClick={() => window.open(designUrl, '_blank')}
          >
            <ExternalLink className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}
